import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ConstructionIcon from "@mui/icons-material/Construction";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SearchIcon from "@mui/icons-material/Search";
import { useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { useToolServiceStatuses } from "../../../hooks/useToolServiceStatuses";
import { fetchTools } from "../../../fetchers/tools";
import { CmmsEmptyState, CmmsErrorState } from "../../cmms/components/CmmsStates";
import {
  ToolServiceStatusLevel,
  groupStatusesByToolId,
  isToolServiceAlerting,
} from "../../cmms/toolServiceStatus";
import {
  cmmsTypeScale,
  getAssetHierarchyHeaderSx,
  getAssetHierarchyPalette,
  getPageHeaderIconSx,
  getPageSubtitleSx,
  getPageTitleSx,
  getStatLabelSx,
  getStatValueSx,
  getStatisticTileSx,
  getSwitchSx,
} from "../../cmms/assetHierarchyStyles";
import messages from "../messages";
import { filterTools } from "../toolSearch";
import CmmsToolsDataGrid from "./CmmsToolsDataGrid";
import ToolServiceDrawer from "./ToolServiceDrawer";

/*
 * The CMMS tool register: which tools exist, how far each has run, and what has been serviced.
 *
 * Read-only, deliberately. Creating and editing a tool is Role_Superuser on the server and stays
 * on /tools under Settings; this page exists because the people who need to *read* a tool's
 * service state — technicians above all — cannot reach that route, and because nothing inside the
 * CMMS menu answered "which tools are near service" at all.
 *
 * Same split as the spare parts register: index.jsx is the shell, this does the work.
 */
export default function ToolsPanel() {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const [companyKey] = useLocalStorage("companyKey", 1);

  const [searchText, setSearchText] = useState("");
  const [watchedOnly, setWatchedOnly] = useState(false);
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [detailsToolId, setDetailsToolId] = useState(null);

  // Same key as /tools, so whichever page is opened first warms the other. A shorter staleTime
  // than that page's `Infinity`: this one is opened to see a current picture, and the register
  // changes under it when someone adds a tool on the settings page.
  const toolsQuery = useQuery(
    ["tools", companyKey],
    () => fetchTools(companyKey),
    { enabled: Boolean(companyKey), staleTime: 30000 },
  );

  // No toolId: every watched tool in the company, which is exactly what a register wants. The
  // hook gates itself on the feature flag and the CMMS role, so this makes no request on an
  // install without the module.
  const serviceStatuses = useToolServiceStatuses();

  const tools = useMemo(() => toolsQuery.data ?? [], [toolsQuery.data]);

  /*
   * The register joined to its readings, one row per tool.
   *
   * Both shapes are kept on the row because the page asks two different questions of them. The
   * grid shows `status` — the worst schedule, the fold the server already ranks for — and the
   * drawer shows `statuses`, all of them, because a tool watched in hours and in cycles has two
   * and the bar can only draw one.
   */
  const rows = useMemo(() => {
    const byTool = groupStatusesByToolId(serviceStatuses.statuses);

    return tools.map((tool) => ({
      ...tool,
      status: serviceStatuses.statusByToolId.get(tool.id) ?? null,
      statuses: byTool.get(tool.id) ?? [],
    }));
  }, [tools, serviceStatuses.statuses, serviceStatuses.statusByToolId]);

  // Client-side. Both lists arrive whole, so the filters narrow something we are holding rather
  // than asking either endpoint a question it has already answered.
  const visibleRows = useMemo(
    () => filterTools(rows, { search: searchText, watchedOnly, attentionOnly }),
    [rows, searchText, watchedOnly, attentionOnly],
  );

  /*
   * Counted per tool off the fold, not per reading.
   *
   * A tool watched by two schedules that are both overdue is one tool to go and service, not two.
   * Counting the readings would report more overdue tools than the register contains.
   */
  const counts = useMemo(() => {
    let overdue = 0;
    let dueSoon = 0;
    let watched = 0;

    rows.forEach((row) => {
      if (!row.status) return;
      watched += 1;
      if (row.status.level === ToolServiceStatusLevel.Overdue) overdue += 1;
      else if (isToolServiceAlerting(row.status.level)) dueSoon += 1;
    });

    return { overdue, dueSoon, watched };
  }, [rows]);

  /*
   * The drawer follows the row, not a copy of it.
   *
   * Holding the id and looking the row up again on every render is what keeps the schedules in
   * the drawer current when the status query refetches on its own minute. Keeping the row object
   * in state would leave the drawer showing the reading the tool had when it was opened.
   */
  const detailsTool = useMemo(
    () => rows.find((row) => row.id === detailsToolId) ?? null,
    [rows, detailsToolId],
  );

  const hasActiveFilters = watchedOnly || attentionOnly || searchText.trim() !== "";

  const handleClearFilters = () => {
    setWatchedOnly(false);
    setAttentionOnly(false);
    setSearchText("");
  };

  return (
    <Box>
      <Box sx={getAssetHierarchyHeaderSx(cmmsColors)}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <ConstructionIcon sx={getPageHeaderIconSx(cmmsColors)} />
            <Box>
              <Typography variant={cmmsTypeScale.pageTitle} sx={getPageTitleSx(cmmsColors)}>
                <FormattedMessage {...messages.pageTitle} />
              </Typography>
              <Typography variant={cmmsTypeScale.pageSubtitle} sx={getPageSubtitleSx(cmmsColors)}>
                <FormattedMessage {...messages.pageSubtitle} />
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {/*
              * Overdue and due-soon are separate tiles rather than one alert count. They call for
              * different things — one is a job to schedule, the other a job that is late — and a
              * single number would let a growing overdue count hide behind a shrinking warning one.
              */}
            <Box sx={getStatisticTileSx(cmmsColors)}>
              <Typography variant={cmmsTypeScale.statLabel} sx={getStatLabelSx(cmmsColors)}>
                <FormattedMessage {...messages.statOverdue} />
              </Typography>
              <Typography
                variant={cmmsTypeScale.statValue}
                sx={{ ...getStatValueSx(cmmsColors), ...(counts.overdue > 0 ? { color: "error.main" } : {}) }}
              >
                {counts.overdue}
              </Typography>
            </Box>

            <Box sx={getStatisticTileSx(cmmsColors)}>
              <Typography variant={cmmsTypeScale.statLabel} sx={getStatLabelSx(cmmsColors)}>
                <FormattedMessage {...messages.statDueSoon} />
              </Typography>
              <Typography
                variant={cmmsTypeScale.statValue}
                sx={{ ...getStatValueSx(cmmsColors), ...(counts.dueSoon > 0 ? { color: "warning.main" } : {}) }}
              >
                {counts.dueSoon}
              </Typography>
            </Box>

            {/*
              * Watched over total, not watched alone. The bare number says nothing without the
              * register behind it — 40 watched tools is thorough out of 45 and negligible out of
              * 1 465, and this module's customer has the latter.
              */}
            <Box sx={getStatisticTileSx(cmmsColors)}>
              <Typography variant={cmmsTypeScale.statLabel} sx={getStatLabelSx(cmmsColors)}>
                <FormattedMessage {...messages.statWatched} />
              </Typography>
              <Typography variant={cmmsTypeScale.statValue} sx={getStatValueSx(cmmsColors)}>
                {counts.watched} / {tools.length}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          sx={{ mt: 2, pt: 2, borderTop: `1px solid ${cmmsColors.border}`, gap: 1 }}
        >
          {/* Controlled: Clear filters has to be able to empty it, otherwise the button leaves
              the register looking filtered with the reason still sitting in the field. */}
          <TextField
            size="small"
            sx={{ width: { xs: "100%", sm: 420 } }}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            label={intl.formatMessage(messages.filterSearch)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Two switches, not one three-way control: "does anything measure this tool" and "is it
              saying something" are different questions, and a healthy watched tool answers yes to
              one and no to the other. */}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={watchedOnly}
                onChange={(event) => setWatchedOnly(event.target.checked)}
                sx={getSwitchSx(cmmsColors)}
              />
            }
            label={intl.formatMessage(messages.filterWatchedOnly)}
          />

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={attentionOnly}
                onChange={(event) => setAttentionOnly(event.target.checked)}
                sx={getSwitchSx(cmmsColors)}
              />
            }
            label={intl.formatMessage(messages.filterAttentionOnly)}
          />
        </Stack>
      </Box>

      <Box sx={{ mt: 2 }}>
        {toolsQuery.error &&
          <CmmsErrorState
            error={toolsQuery.error}
            fallback={intl.formatMessage(messages.loadFailed)}
            onRetry={() => toolsQuery.refetch()}
            isRetrying={toolsQuery.isFetching}
          />
        }

        {/*
          * A failed status read does not blank the page — the register is still worth reading, and
          * it is the half that always loads. But it does need saying out loud: without this
          * banner every row falls back to ToolServiceStatusCell's em dash, whose tooltip claims
          * "no usage-based schedule watches this tool". That is a lie when the read simply failed,
          * and it is the reassuring kind.
          */}
        {!toolsQuery.error && serviceStatuses.isError &&
          <Box sx={{ mb: 2 }}>
            <CmmsErrorState
              dense
              error={serviceStatuses.error}
              fallback={intl.formatMessage(messages.statusLoadFailed)}
              onRetry={() => serviceStatuses.refetch()}
            />
          </Box>
        }

        {!toolsQuery.error &&
          <CmmsToolsDataGrid
            rows={visibleRows}
            // First load only, so a background refetch does not replace the list with a skeleton.
            isLoading={toolsQuery.isLoading}
            statusLoading={serviceStatuses.isLoading}
            onOpenDetails={(row) => setDetailsToolId(row.id)}
            emptyState={
              hasActiveFilters
                ? <CmmsEmptyState
                    icon={<FilterAltOffIcon />}
                    title={intl.formatMessage(messages.emptyFilteredTitle)}
                    hint={intl.formatMessage(messages.emptyFilteredHint)}
                    action={
                      <Button size="small" variant="outlined" color="secondary" onClick={handleClearFilters}>
                        <FormattedMessage {...messages.filterClear} />
                      </Button>
                    }
                  />
                : <CmmsEmptyState
                    icon={<ConstructionIcon />}
                    title={intl.formatMessage(messages.emptyTitle)}
                    // The hint has to point somewhere: this page cannot create a tool, so an
                    // empty register here is a dead end without it.
                    hint={intl.formatMessage(messages.emptyHint)}
                  />
            }
          />
        }
      </Box>

      <ToolServiceDrawer
        tool={detailsTool}
        onClose={() => setDetailsToolId(null)}
      />
    </Box>
  );
}
